import React, { useEffect, useState } from 'react';
import { Badge, Card, Col, Row, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import axios from 'axios';

const CATEGORY_LABELS = { BUG: 'Bugs', IDEA: 'Ideas', COMPLAINT: 'Complaints', OTHER: 'Help', CONTACT: 'Enquiries' };
const statusBadge = (status) => ({ NEW: 'warning', IN_PROGRESS: 'primary', REOPEN: 'danger' }[status] || 'secondary');

function Figure({ value, label, hint, tone, testId }) {
  return (
    <Col>
      <Card className="text-center h-100" data-testid={testId}>
        <Card.Body>
          <h3 className={tone ? `text-${tone}` : undefined}>{value}</h3>
          <p className="mb-0">{label}</p>
          {hint && <small className="text-muted">{hint}</small>}
        </Card.Body>
      </Card>
    </Col>
  );
}

const days = (n) => (n === 1 ? '1 day' : `${n} days`);
const ago = (n) => (n === 0 ? 'today' : `${days(n)} ago`);

/** The Tech Support dashboard's tickets: what is open, stale, waiting on the sender and closed, and the ones that have waited longest. */
export default function SupportTickets({ ticketsAddress }) {
  const [s, setS] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    axios.get('/api/system-admin/feedback/stats').then((r) => setS(r.data)).catch(() => setFailed(true));
  }, []);

  if (failed || !s) return null;

  const categories = Object.entries(s.openByCategory || {});
  return (
    <div className="mt-3" data-testid="support-tickets">
      <div className="d-flex justify-content-between align-items-baseline">
        <h5>Support tickets</h5>
        <Link to={ticketsAddress}>Open the inbox</Link>
      </div>
      <Row xs={2} md={4} className="g-3">
        <Figure
          testId="tickets-open" value={s.open} label="Open"
          hint={`${s.new} new, ${s.inProgress} in progress, ${s.reopened} reopened`}
        />
        <Figure
          testId="tickets-stale" value={s.stale} label="Stale" tone={s.stale > 0 ? 'danger' : undefined}
          hint={`open, nothing done for ${s.staleDays}+ days`}
        />
        <Figure
          testId="tickets-waiting" value={s.waitingOnSender} label="Resolved, waiting on the sender"
          hint={`closes by itself after ${s.autoCloseDays} days`}
        />
        <Figure
          testId="tickets-closed" value={s.closedLast7Days} label="Closed this week"
          hint={`${s.closedLast30Days} in 30 days, ${s.closed} in all`}
        />
      </Row>

      {categories.length > 0 && (
        <div className="mt-3 d-flex flex-wrap gap-2 align-items-center small">
          <span className="text-muted">Open by type:</span>
          {categories.map(([key, count]) => <Badge key={key} bg="secondary">{CATEGORY_LABELS[key] || key} {count}</Badge>)}
          {s.oldestOpenDays > 0 && <span className="text-muted ms-2">Oldest open ticket: {days(s.oldestOpenDays)} old</span>}
        </div>
      )}

      {s.longestWaiting.length > 0 && (
        <Card className="mt-3">
          <Card.Header><h6 className="mb-0">Waiting longest</h6></Card.Header>
          <Table hover responsive className="mb-0 align-middle" data-testid="tickets-longest">
            <thead>
              <tr><th>#</th><th>Subject</th><th>From</th><th>Status</th><th className="text-nowrap">Last activity</th><th>Open for</th></tr>
            </thead>
            <tbody>
              {s.longestWaiting.map((t) => (
                <tr key={t.id}>
                  <td className="text-muted">{t.id}</td>
                  <td style={{ maxWidth: '24rem' }}><Link to={`${ticketsAddress}?ticket=${t.id}`} className="fw-semibold text-decoration-none">{t.subject}</Link></td>
                  <td className="small">{t.from}{t.organizationName ? <div className="text-muted">{t.organizationName}</div> : null}</td>
                  <td><Badge bg={statusBadge(t.status)}>{t.statusLabel}</Badge></td>
                  <td className="text-nowrap">{t.daysSinceUpdate >= s.staleDays ? <span className="text-danger">{ago(t.daysSinceUpdate)}</span> : ago(t.daysSinceUpdate)}</td>
                  <td className="text-nowrap">{t.daysOpen === 0 ? 'today' : days(t.daysOpen)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}
    </div>
  );
}
